import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { TeamCard } from '../components/team/TeamCard'
import { teamMembers as fallbackTeam } from '../data/team'
import type { TeamMember } from '../data/team'
import { API_BASE, apiAssetUrl } from '../config'

export function Team({ variant = 'full' }: { variant?: 'home' | 'full' }) {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>(fallbackTeam)

  useEffect(() => {
    let cancelled = false
    fetch(`${API_BASE}/api/public/team`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || !data?.success || !Array.isArray(data.team)) return
        setTeamMembers(
          data.team.map((t: { id: number; name: string; role: string; bio?: string; image: string; linkedin?: string }) => ({
            id: String(t.id),
            name: t.name,
            role: t.role,
            bio: t.bio,
            image: apiAssetUrl(t.image) || `/team/placeholder.webp`,
            linkedin: t.linkedin,
          }))
        )
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  const founders = teamMembers.slice(0, 2)
  const rest = teamMembers.slice(2)

  return (
    <section className="section-pad px-4 sm:px-6 lg:px-8 bg-slate-50/50 dark:bg-slate-900/30 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_100%,rgba(26,69,176,0.06),transparent)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_100%,rgba(255,215,0,0.04),transparent)] pointer-events-none" />
      <div className="container-wide relative">
        <motion.div
          className="text-center mb-12 sm:mb-16"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-accent font-semibold text-sm uppercase tracking-wider">Our People</span>
          <h2 className="section-heading mt-2 mb-3">
            The People Behind DigiSutra
          </h2>
          <p className="section-sub max-w-xl mx-auto">
            Strategy, creativity, and execution — meet the team that turns your growth goals into reality.
          </p>
        </motion.div>

        {/* CEO & COO: 2-card grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8 max-w-4xl mx-auto">
          {founders.map((member, i) => (
            <TeamCard key={member.id} member={member} delay={i * 0.1} imageFit="contain" />
          ))}
        </div>

        {/* Rest of team: horizontal scroll only (no loop, no auto “2 sets”) */}
        {variant === 'full' && (
          <div className="relative -mx-4 sm:-mx-6 lg:-mx-8">
            <div className="flex gap-4 sm:gap-6 overflow-x-auto overflow-y-hidden pb-2 px-4 sm:px-6 lg:px-8 scroll-smooth team-scroll">
              {rest.map((member, i) => (
                <motion.div
                  key={member.id}
                  className="flex-shrink-0 w-[280px] sm:w-[300px]"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-20px' }}
                  transition={{ delay: Math.min(i * 0.05, 0.35) }}
                >
                  <TeamCard member={member} delay={0} imageFit="cover" />
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {variant === 'home' && (
          <motion.div
            className="text-center mt-10 sm:mt-12"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/about" className="btn-secondary group">
              Meet the Full Team
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  )
}
